import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversations } from '@/hooks/useConversations';
import { useWorkspace } from '@/context';
import type { FlowId } from '@/flows/types';
import { getFlowRuntime } from '@/flows/runtime/registry';
import { setFlowPrefill, type FlowPrefill } from '@/utils/flowPrefill';
import { storeConversationAttachment } from '@/utils/conversationAttachment';

export interface StartFlowOptions {
  prefill?: FlowPrefill;
  /** @deprecated Use prefill */
  seedDraft?: FlowPrefill;
  /** File attached in the composer — stored for the conversation and sent with the flow. */
  attachment?: File | null;
}

export function useStartFlow(flowId: FlowId) {
  const runtime = getFlowRuntime(flowId);
  const navigate = useNavigate();
  const { effectiveRole } = useWorkspace();
  const { createConversation } = useConversations(effectiveRole);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const start = useCallback(
    async (message = runtime.start.defaultStarterMessage, options?: StartFlowOptions) => {
      setIsStarting(true);
      setError(null);

      try {
        let conversationId: string;
        try {
          const conv = await createConversation({ title: runtime.start.conversationTitle });
          conversationId = conv.id;
        } catch {
          conversationId = runtime.start.createLocalConversationId();
        }

        runtime.start.clearStoredState(conversationId);
        runtime.start.registerConversation(conversationId);

        const draftPrefill = options?.prefill ?? options?.seedDraft;
        if (draftPrefill && Object.keys(draftPrefill).length > 0) {
          setFlowPrefill(flowId, conversationId, draftPrefill);
        }

        if (options?.attachment) {
          await storeConversationAttachment(conversationId, options.attachment);
        }

        navigate(runtime.start.buildNavigateUrl(conversationId, message, effectiveRole));
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not start flow';
        setError(msg);
        console.error(e);
      } finally {
        setIsStarting(false);
      }
    },
    [createConversation, effectiveRole, flowId, navigate, runtime]
  );

  return { start, isStarting, error };
}
